import { CharStream, CommonTokenStream, ConsoleErrorListener, Token } from "antlr4ng";
import { RuleGrammarLexer } from "./generated/RuleGrammarLexer";
import { RuleGrammarParser as AntlrParser } from "./generated/RuleGrammarParser";

/** Un token TAG: contiene l'id del tag (senza le doppie parentesi graffe). */
export interface TagScriptToken {
  type: "TAG";
  tagId: string;
}

/** Un operatore (es. +, -, *, /, (, ), >, <, =, !=, <=, >=, ≠). */
export interface OperatorScriptToken {
  type: "OPERATOR";
  text: string;
}

/** Un token di testo libero (numeri, spazi, ecc.). */
export interface TextScriptToken {
  type: "TEXT";
  text: string;
}

export type ScriptToken = TagScriptToken | OperatorScriptToken | TextScriptToken;

/** Tipi di token ANTLR classificati come operatori. */
const OPERATOR_TOKEN_TYPES = new Set([
  RuleGrammarLexer.COMPARE_OP,
  RuleGrammarLexer.PLUS,
  RuleGrammarLexer.MINUS,
  RuleGrammarLexer.STAR,
  RuleGrammarLexer.SLASH,
  RuleGrammarLexer.LPAREN,
  RuleGrammarLexer.RPAREN,
]);

/**
 * Sostituisce il ConsoleErrorListener predefinito di ANTLR.
 * Raccoglie tutti gli errori sintattici in un array invece di stamparli su stderr.
 */
class ErrorCollector extends ConsoleErrorListener {
  readonly errors: string[] = [];

  override syntaxError(
    _recognizer: unknown,
    _offendingSymbol: unknown,
    line: number,
    charPositionInLine: number,
    msg: string
  ): void {
    this.errors.push(`line ${line}:${charPositionInLine} ${msg}`);
  }
}

/**
 * Punto di estensione per vincoli non esprimibili nella grammatica LL.
 * Attualmente vuoto: tutti i vincoli sono gestiti direttamente da ANTLR.
 */
function runExtraValidations(_tokens: Token[]): string | null {
  return null;
}

/**
 * Valida uno script di regola rispetto alla grammatica RuleGrammar.
 *
 * @param script - La stringa grezza della regola inserita dall'utente.
 * @returns `true`  se la regola è sintatticamente valida.
 *          `false` se la regola contiene errori sintattici o viola i vincoli.
 *          Gli errori vengono loggati in console.error con un messaggio descrittivo.
 *
 * Le stringhe vuote o composte solo da spazi sono considerate valide (non ancora scritte).
 */
export function parseRule(script: string): boolean {
  if (typeof script !== "string") return true;
  if (!script.trim()) {
    return true;
  }

  // Analisi lessicale (Lexer)
  const inputStream = CharStream.fromString(script);
  const lexer = new RuleGrammarLexer(inputStream);

  const lexerErrors = new ErrorCollector();
  lexer.removeErrorListeners();
  lexer.addErrorListener(lexerErrors);

  const tokenStream = new CommonTokenStream(lexer);
  tokenStream.fill();

  if (lexerErrors.errors.length > 0) {
    lexerErrors.errors.forEach((e) => console.error("[RuleGrammarParser][parseRule] Lexical error:", e));
    return false;
  }

  // Controllo token INVALID
  // INVALID è la regola catch-all del lexer: qualsiasi carattere non riconosciuto
  // dalla grammatica (lettere libere, @, ecc.) produce questo token.
  // Ogni token INVALID viene loggato individualmente.
  const allTokens = tokenStream.getTokens();
  const invalidTokens = allTokens.filter((t) => t.type === RuleGrammarLexer.INVALID);
  if (invalidTokens.length > 0) {
    invalidTokens.forEach((t) =>
      console.error(
        `[RuleGrammarParser][parseRule] Invalid token "${t.text}" at position ${t.column} — text outside tags must use {{...}} syntax`
      )
    );
    return false;
  }

  // Analisi sintattica (Parser)
  const parser = new AntlrParser(tokenStream);

  const parserErrors = new ErrorCollector();
  parser.removeErrorListeners();
  parser.addErrorListener(parserErrors);

  parser.script();

  if (parserErrors.errors.length > 0) {
    parserErrors.errors.forEach((e) => console.error("[RuleGrammarParser][parseRule] Syntax error:", e));
    return false;
  }

  // Validazioni extra
  const extraError = runExtraValidations(allTokens);
  if (extraError) {
    console.error("[RuleGrammarParser][parseRule]", extraError);
    return false;
  }

  return true;
}

/**
 * Tokenizza uno script di regola in una sequenza di ScriptToken (TAG | OPERATOR | TEXT).
 * Usato dai componenti React per il rendering: i TAG diventano pill colorati,
 * gli OPERATOR vengono resi in grassetto e blu, il TEXT viene reso come stringa semplice.
 *
 * La gestione degli spazi è identica all'originale: i WS (già esclusi da tokenizeRule)
 * vengono reinseriti come separatori nei TEXT, aggregati al token precedente oppure
 * preposti al token successivo — nessun cambiamento rispetto al comportamento originale.
 */
export function tokenizeScriptForRendering(script: string): ScriptToken[] {
  if (typeof script !== "string") return [];

  const result: ScriptToken[] = [];
  const tokens = _tokenizeRuleWithTypes(script);

  for (let i = 0; i < tokens.length; i++) {
    const { text, isTag, isOperator } = tokens[i];
    if (isTag) {
      const tagId = text.slice(2, -2).trim();
      result.push({ type: "TAG", tagId });
    } else if (isOperator) {
      result.push({ type: "OPERATOR", text });
    } else {
      result.push({ type: "TEXT", text: text.trim() });
    }
    // Aggiunge uno spazio dopo ogni token, tranne l'ultimo
    if (i < tokens.length - 1) {
      result.push({ type: "TEXT", text: " " });
    }
  }

  return result;
}

/**
 * Versione interna che tokenizza lo script preservando il tipo di ogni token.
 * Esclude WS e EOF (come tokenizeRule) e aggiunge i flag isTag e isOperator.
 */
function _tokenizeRuleWithTypes(script: string): { text: string; isTag: boolean; isOperator: boolean }[] {
  if (typeof script !== "string") return [];

  const inputStream = CharStream.fromString(script);
  const lexer = new RuleGrammarLexer(inputStream);
  lexer.removeErrorListeners();

  const tokenStream = new CommonTokenStream(lexer);
  tokenStream.fill();

  return tokenStream
    .getTokens()
    .filter((t) => t.type !== RuleGrammarLexer.WS && t.type !== Token.EOF)
    .map((t) => ({
      text: t.text ?? "",
      isTag: t.type === RuleGrammarLexer.TAG,
      isOperator: OPERATOR_TOKEN_TYPES.has(t.type),
    }));
}

/**
 * Tokenizza uno script di regola tramite il lexer ANTLR e restituisce
 * i testi dei token significativi (esclude WS e EOF).
 * Usato da normalizeRule: join(" ") garantisce esattamente uno spazio tra ogni
 * token. Gli operatori composti (!=, >=, <=, ≠) sono già un unico token ANTLR
 * e restano quindi attaccati come previsto.
 */
export function tokenizeRule(script: string): string[] {
  if (typeof script !== "string") {
    return [];
  }

  const inputStream = CharStream.fromString(script);
  const lexer = new RuleGrammarLexer(inputStream);
  lexer.removeErrorListeners();

  const tokenStream = new CommonTokenStream(lexer);
  tokenStream.fill();

  return tokenStream
    .getTokens()
    .filter((t) => t.type !== RuleGrammarLexer.WS && t.type !== Token.EOF)
    .map((t) => t.text ?? "");
}
